import { useMemo } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import {
  type Block as ContentfulBlock,
  BLOCKS,
  type Inline as ContentfulInline,
  type Text as ContentfulText
} from '@contentful/rich-text-types';
import { motion } from 'framer-motion';
import { type IGatsbyImageData } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { InlineImage } from '@/components/blog/InlineImage';
import { Button, GradientHeader, Text } from '@/components/design-system';
import { HeadingFourStyles } from '@/components/design-system/Heading';
import { ANIMATIONS } from '@/utils';

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 60px;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  margin: 0 auto;
  padding: 0 40px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    grid-template-columns: 1fr;
  }
`;

const H3 = styled(GradientHeader)`
  margin: 64px 0 16px;
  ${HeadingFourStyles}

  &:first-child {
    margin-top: 0;
  }

  &&& {
    text-align: left;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    margin: 48px 0 16px;

    &:first-child {
      margin-top: 0;
    }
  }
`;

const RichTextContainer = styled.div`
  ul {
    list-style: outside;
    padding-inline-start: 24px;
    margin-bottom: 20px;
  }

  p {
    /* @todo replace this with 1rem when we update self serve to use the new page wrapper */
    font-size: 16px;
    line-height: ${({ theme }) => theme.lineHeight.body.base};
    font-weight: ${({ theme }) => theme.fontWeight.body};
  }
`;

const ImageWrapper = styled.div`
  margin: 32px 0 24px 0;

  &:only-child {
    margin-top: 0;
  }
`;

const StyledButton = styled(Button)`
  width: fit-content;
`;

interface GatsbyImageEmbed {
  description: string;
  gatsbyImageData: IGatsbyImageData;
  mimeType: string;
  title: string;
  url: string;
}

function textFromNode(
  node: ContentfulBlock | ContentfulInline | ContentfulText
): string {
  if ('value' in node) return node.value;
  return node.content.reduce((acc, n) => (acc += textFromNode(n)), '');
}

function RichTextContent({
  raw,
  references = []
}: {
  raw: string;
  references: Record<string, unknown>[];
}) {
  const content = useMemo(() => {
    const document = JSON.parse(raw);

    return documentToReactComponents(document, {
      renderNode: {
        [BLOCKS.HEADING_3]: (_, children) => {
          return <H3 renderAs="h3">{children}</H3>;
        },

        [BLOCKS.PARAGRAPH]: (node, children) => {
          // If only content is a hyperlink, render a button.
          const hyperlinkOnlyContent = node.content.filter(
            n =>
              (n.nodeType === 'text' && n.value) || n.nodeType === 'hyperlink'
          );
          if (
            hyperlinkOnlyContent.length &&
            hyperlinkOnlyContent.every(n => n.nodeType === 'hyperlink')
          ) {
            const hyperlinkNode = hyperlinkOnlyContent.find(
              n => n.nodeType === 'hyperlink'
            )!;
            const link = hyperlinkNode.data.uri || '#';
            const text = textFromNode(hyperlinkNode);
            return <StyledButton link={link} text={text} />;
          }

          return <Text size="medium">{children}</Text>;
        },

        [BLOCKS.EMBEDDED_ASSET]: node => {
          const asset = references.find(
            ref => ref.contentful_id === node.data.target.sys.id
          ) as GatsbyImageEmbed | undefined;

          switch (asset?.mimeType) {
            case 'image/jpeg':
            case 'image/gif':
            case 'image/png':
              return (
                <ImageWrapper>
                  <InlineImage
                    alt={asset.title}
                    caption={asset.description}
                    gatsbyImageData={asset.gatsbyImageData}
                    src={asset.url}
                  />
                </ImageWrapper>
              );

            default:
              return null;
          }
        }
      }
    });
  }, [raw, references]);

  return <RichTextContainer>{content}</RichTextContainer>;
}

interface TwoUpRichTextProps {
  column1RichText?: {
    raw: string;
    references: Record<string, unknown>[];
  };
  column2RichText?: {
    raw: string;
    references: Record<string, unknown>[];
  };
}

export function TwoUpRichText(props: TwoUpRichTextProps) {
  return (
    <Wrapper {...ANIMATIONS.SimpleFadeIn} as={motion.div}>
      {props.column1RichText ? (
        <RichTextContent
          raw={props.column1RichText.raw}
          references={props.column1RichText.references}
        />
      ) : null}
      {props.column2RichText ? (
        <RichTextContent
          raw={props.column2RichText.raw}
          references={props.column2RichText.references}
        />
      ) : null}
    </Wrapper>
  );
}
